import { PropsWithChildren, createContext, useMemo } from 'react';

import { isEmpty } from 'lodash-es';
import { useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { ApolloError } from '@apollo/client';

import { useProgramQuery } from '@/generated';
import { ProgramType } from '@/pages/ProgramCreatePage/utils';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';
import {
  programFieldsConfigAtom,
  showAudienceExclusionSectionAtom,
} from '@/store/Programs';

type ProgramContext = {
  program: ProgramType | undefined;
  isProgramLoading: boolean;
  isProgramError: ApolloError | undefined;
  isMaxQuietPeriodReached: boolean;
};

export const ProgramContext = createContext<ProgramContext | undefined>(
  undefined,
);

const ProgramProvider = ({ children }: PropsWithChildren) => {
  const setShowAudienceExclusionSection = useSetRecoilState(
    showAudienceExclusionSectionAtom,
  );
  const setProgramFieldsConfig = useSetRecoilState(programFieldsConfigAtom);
  const { programId = '' } = useParams();
  const snackbar = useSnackbar();

  const {
    data: { program } = {},
    loading: isProgramLoading,
    error: isProgramError,
  } = useProgramQuery({
    variables: { programId },
    errorPolicy: 'all',
    onCompleted: ({ program }) => {
      if (!isEmpty(program.excludedAudiences)) {
        setShowAudienceExclusionSection(true);
      }
      setProgramFieldsConfig(program.fields ?? []);
    },
    onError: (e) => snackbar({ message: e.message, severity: 'error' }),
  });

  const isMaxQuietPeriodReached = (program?.quietPeriodRules?.length ?? 0) >= 5;

  const context: ProgramContext = useMemo(() => {
    return {
      program,
      isProgramLoading,
      isProgramError,
      isMaxQuietPeriodReached,
    };
  }, [program, isProgramLoading, isProgramError, isMaxQuietPeriodReached]);

  return (
    <ProgramContext.Provider value={context}>
      {children}
    </ProgramContext.Provider>
  );
};

export default ProgramProvider;
